/* Glow On theme script: drawer cart (Shopify AJAX Cart API + Section Rendering), saved items,
   predictive search, product variant picker, and the home page's boutique, lab, ritual and diary. */
const $ = (s, r = document) => r.querySelector(s);
const $$ = (s, r = document) => [...r.querySelectorAll(s)];
const B = window.GlowOn || { routes: {} };
const ROOT = (B.routes.root || '/').replace(/\/?$/, '/');
const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)');
const finePointer = matchMedia('(hover: hover) and (pointer: fine)');
const scrollBehavior = () => (reduceMotion.matches ? 'auto' : 'smooth');
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const plural = (n, one, many = one + 's') => `${n} ${n === 1 ? one : many}`;

const store = {
  get(key, fallback) { try { const v = localStorage.getItem(key); return v ? JSON.parse(v) : fallback; } catch { return fallback; } },
  set(key, value) { try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* storage unavailable: keep state in memory */ } },
};

const ICON = {
  plus: '<svg class="ico" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 5.5v13M5.5 12h13"/></svg>',
  pause: '<svg class="ico" viewBox="0 0 24 24" aria-hidden="true"><path d="M9 6.5v11M15 6.5v11"/></svg>',
  play: '<svg class="ico" viewBox="0 0 24 24" aria-hidden="true"><path d="M8.5 6.5v11l9-5.5z"/></svg>',
};

/* ---------- money ---------- */
function formatMoney(cents, format = B.moneyFormat || '${{amount}}') {
  const value = Number(cents || 0) / 100;
  const fmt = (n, decimals, thousands = ',', decimal = '.') => {
    const [int, frac] = n.toFixed(decimals).split('.');
    const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return frac ? grouped + decimal + frac : grouped;
  };
  const patterns = {
    amount: () => fmt(value, 2),
    amount_no_decimals: () => fmt(value, 0),
    amount_with_comma_separator: () => fmt(value, 2, '.', ','),
    amount_no_decimals_with_comma_separator: () => fmt(value, 0, '.', ','),
    amount_with_apostrophe_separator: () => fmt(value, 2, "'", '.'),
    amount_no_decimals_with_space_separator: () => fmt(value, 0, ' '),
    amount_with_space_separator: () => fmt(value, 2, ' ', ','),
    amount_with_period_and_space_separator: () => fmt(value, 2, ' ', '.'),
  };
  return format.replace(/<[^>]*>/g, '').replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => (patterns[key] || patterns.amount)());
}

/* ---------- toast ---------- */
let toastTimer = 0;
function toast(message) {
  if ($('dialog[open]')) return; /* modals sit above any toast, so dialogs give their own inline feedback */
  const el = $('#toast');
  if (!el) return;
  el.innerHTML = `<p>${esc(message)}</p>`;
  requestAnimationFrame(() => el.classList.add('show'));
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => el.classList.remove('show'), 3800);
}

/* ---------- dialogs ---------- */
$$('dialog').forEach((d) => d.addEventListener('click', (e) => { if (e.target === d) d.close(); }));

/* ---------- bag drawer ---------- */
const bag = $('#bag-dialog');
const cartState = { undo: null };

function updateBagCount(n) {
  const badge = $('#bag-count');
  if (badge) { badge.textContent = n; badge.hidden = !n; }
  $('#bag-btn')?.setAttribute('aria-label', `Bag, ${plural(n, 'item')}`);
  const tabN = $('#tab-bag-n');
  if (tabN) tabN.textContent = n;
}

function renderDrawer(html) {
  if (!html || !bag) return;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const fresh = doc.querySelector('#cart-panel');
  const current = $('#cart-panel');
  if (!fresh || !current) return;
  fresh.hidden = current.hidden;
  current.replaceWith(fresh);
  updateBagCount(Number(fresh.dataset.count || 0));
  if (cartState.undo) {
    fresh.insertAdjacentHTML('afterbegin', `<div class="undo-row" role="status"><span>Removed ${esc(cartState.undo.title)}.</span><button class="link-btn" type="button" data-undo>Undo</button></div>`);
  }
}

async function cartRequest(url, body) {
  const isForm = body instanceof FormData;
  if (isForm) { body.append('sections', 'cart-drawer'); body.append('sections_url', location.pathname); }
  const res = await fetch(url, {
    method: 'POST',
    headers: isForm ? { Accept: 'application/json' } : { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: isForm ? body : JSON.stringify({ ...body, sections: 'cart-drawer', sections_url: location.pathname }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.status) throw new Error(data.description || data.message || 'That didn’t go through. Try again in a moment.');
  return data;
}

async function addToCart(body, { open = true } = {}) {
  const data = await cartRequest(`${B.routes.cartAdd}.js`, body);
  cartState.undo = null;
  renderDrawer(data.sections?.['cart-drawer']);
  if (open) openBag('bag');
  return data;
}

async function changeLine(key, quantity) {
  const panel = $('#cart-panel');
  panel?.classList.add('is-busy');
  try {
    const data = await cartRequest(`${B.routes.cartChange}.js`, { id: key, quantity });
    renderDrawer(data.sections?.['cart-drawer']);
    return true;
  } catch (err) {
    panel?.classList.remove('is-busy');
    const line = $(`[data-line-key="${CSS.escape(key)}"]`);
    line?.querySelector('.line-error')?.remove();
    line?.querySelector('.line-info')?.insertAdjacentHTML('beforeend', `<p class="line-error" role="alert">${esc(err.message)}</p>`);
    return false;
  }
}

function setTab(view) {
  $$('.tab', bag).forEach((t) => {
    const on = t.dataset.view === view;
    t.setAttribute('aria-selected', String(on));
    t.tabIndex = on ? 0 : -1;
  });
  $('#cart-panel').hidden = view !== 'bag';
  $('#saved-panel').hidden = view !== 'saved';
  if (view === 'saved') renderSaved();
}

function openBag(view = 'bag') {
  if (!bag) { location.href = B.routes.cart; return; }
  setTab(view);
  if (!bag.open) bag.showModal();
}

bag?.addEventListener('close', () => { cartState.undo = null; $('.undo-row', bag)?.remove(); });
bag?.addEventListener('keydown', (e) => {
  if (!e.target.closest('.tabs') || !['ArrowLeft', 'ArrowRight'].includes(e.key)) return;
  const next = $('#cart-panel').hidden ? 'bag' : 'saved';
  setTab(next);
  $(`.tab[data-view="${next}"]`, bag).focus();
});

/* ---------- saved items (kept in this browser) ---------- */
let savedList = store.get('glowon.saved', []).filter((h) => typeof h === 'string');
const productCache = new Map();

function syncSaveButtons() {
  $$('[data-save]').forEach((b) => b.setAttribute('aria-pressed', String(savedList.includes(b.dataset.save))));
  const n = savedList.length;
  const badge = $('#saved-count');
  if (badge) { badge.textContent = n; badge.hidden = !n; }
  $('#saved-btn')?.setAttribute('aria-label', `Saved products, ${plural(n, 'item')}`);
  const tabN = $('#tab-saved-n');
  if (tabN) tabN.textContent = n;
}

function toggleSave(handle, btn) {
  const on = !savedList.includes(handle);
  savedList = on ? [...savedList, handle] : savedList.filter((h) => h !== handle);
  store.set('glowon.saved', savedList);
  syncSaveButtons();
  if (btn && on && !reduceMotion.matches) { btn.classList.remove('pop'); void btn.offsetWidth; btn.classList.add('pop'); }
  if (bag?.open && !$('#saved-panel').hidden) renderSaved();
  toast(on ? 'Saved to your list.' : 'Removed from your list.');
}

function sizedImage(src, width) {
  if (!src) return '';
  try { const u = new URL(src, location.href); u.searchParams.set('width', width); return u.toString(); } catch { return src; }
}

async function loadProduct(handle) {
  if (!productCache.has(handle)) {
    productCache.set(handle, fetch(`${ROOT}products/${encodeURIComponent(handle)}.js`).then((r) => (r.ok ? r.json() : null)).catch(() => null));
  }
  return productCache.get(handle);
}

async function renderSaved() {
  const body = $('#saved-panel .side-body');
  if (!body) return;
  if (!savedList.length) {
    body.innerHTML = `<div class="side-empty"><span class="empty-pearl" aria-hidden="true"></span><p class="h3">Nothing Saved Yet</p><p>Tap the heart on any product to keep it here.</p><a class="btn btn-soft" href="${esc(ROOT)}collections/all">Browse the Boutique</a></div>`;
    return;
  }
  body.innerHTML = '<p class="search-status">Loading your saved products…</p>';
  const products = await Promise.all(savedList.map(loadProduct));
  const found = products.filter(Boolean);
  if (found.length !== savedList.length) {
    savedList = found.map((p) => p.handle);
    store.set('glowon.saved', savedList);
    syncSaveButtons();
  }
  body.innerHTML = `<ul class="lines saved-list">${found.map((p) => {
    const single = p.variants.length === 1;
    const variant = p.variants.find((v) => v.available) || p.variants[0];
    const action = !p.available
      ? '<span class="sold-out">Sold out</span>'
      : single
        ? `<button class="add-btn" type="button" data-add-variant="${variant.id}" aria-label="Add ${esc(p.title)} to bag">${ICON.plus}<span aria-hidden="true">Add</span></button>`
        : `<a class="add-btn" href="${esc(p.url)}">Choose Options</a>`;
    return `<li class="line">
      <a class="line-thumb" href="${esc(p.url)}" tabindex="-1" aria-hidden="true">${p.featured_image ? `<img src="${esc(sizedImage(p.featured_image, 200))}" alt="" width="200" height="267" loading="lazy">` : ''}</a>
      <div class="line-info">
        <p class="line-name"><a href="${esc(p.url)}">${esc(p.title)}</a></p>
        <p class="line-opt">${p.type ? `${esc(p.type)} · ` : ''}<span class="price">${formatMoney(p.price)}</span></p>
        <div class="line-row">${action}<button class="link-btn" type="button" data-save="${esc(p.handle)}" aria-pressed="true">Remove</button></div>
      </div></li>`;
  }).join('')}</ul>`;
}

/* ---------- global clicks ---------- */
document.addEventListener('click', async (e) => {
  const t = e.target;
  const opener = t.closest('[data-open-dialog]');
  if (opener) { e.preventDefault(); const d = $(`#${opener.dataset.openDialog}`); if (d === searchDialog) openSearch(); else d?.showModal(); return; }
  if (t.closest('[data-close]')) { t.closest('dialog')?.close(); return; }
  if (t.closest('#bag-btn')) { if (e.metaKey || e.ctrlKey) return; e.preventDefault(); openBag('bag'); return; }
  if (t.closest('#saved-btn')) { openBag('saved'); return; }
  const tab = t.closest('.tab[data-view]');
  if (tab) { setTab(tab.dataset.view); return; }

  const save = t.closest('[data-save]');
  if (save) { e.preventDefault(); toggleSave(save.dataset.save, save); return; }

  const add = t.closest('[data-add-variant]');
  if (add) {
    e.preventDefault();
    add.classList.add('is-loading');
    try { await addToCart({ items: [{ id: Number(add.dataset.addVariant), quantity: 1 }] }); }
    catch (err) { toast(err.message); }
    finally { add.classList.remove('is-loading'); }
    return;
  }

  const lineBtn = t.closest('[data-line]');
  if (lineBtn) {
    const key = lineBtn.dataset.line;
    const qty = Number(lineBtn.dataset.qty);
    const line = lineBtn.closest('[data-line-key]');
    if (qty === 0 && line) cartState.undo = { id: Number(line.dataset.variant), quantity: Number(line.dataset.quantity), title: line.dataset.title };
    const ok = await changeLine(key, qty);
    if (!ok) { cartState.undo = null; return; }
    if (qty === 0) $('[data-undo]', bag)?.focus();
    else ($(`[data-line="${CSS.escape(key)}"][data-qty="${qty + Number(lineBtn.dataset.step || 0)}"]`, bag) || $(`[data-line-key="${CSS.escape(key)}"] .line-name a`, bag))?.focus();
    return;
  }

  if (t.closest('[data-undo]')) {
    const u = cartState.undo;
    cartState.undo = null;
    if (u) { try { await addToCart({ items: [{ id: u.id, quantity: u.quantity }] }, { open: false }); } catch (err) { toast(err.message); } }
    $('#cart-panel .line-name a', bag)?.focus();
    return;
  }

  const navLink = t.closest('dialog a[href^="#"]');
  if (navLink) navLink.closest('dialog').close();
});

/* ---------- predictive search ---------- */
const searchDialog = $('#search-dialog');
const searchInput = $('#search-input');
const searchResults = $('#predictive-results');
let searchAbort = null, searchTimer = 0;

function openSearch() {
  if (!searchDialog) { location.href = B.routes.search; return; }
  searchDialog.showModal();
  searchInput.focus();
  searchInput.select();
}

async function runSearch() {
  const q = searchInput.value.trim();
  if (!q) { searchResults.innerHTML = ''; return; }
  searchAbort?.abort();
  searchAbort = new AbortController();
  searchResults.setAttribute('aria-busy', 'true');
  try {
    const url = `${B.routes.predictiveSearch}?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6&section_id=predictive-search`;
    const html = await fetch(url, { signal: searchAbort.signal }).then((r) => r.text());
    const doc = new DOMParser().parseFromString(html, 'text/html');
    searchResults.innerHTML = doc.querySelector('[data-predictive]')?.innerHTML || '';
  } catch (err) {
    if (err.name !== 'AbortError') searchResults.innerHTML = '<p class="search-status">Search is unavailable right now. Press Enter to see full results.</p>';
  } finally {
    searchResults.removeAttribute('aria-busy');
  }
}
searchInput?.addEventListener('input', () => { clearTimeout(searchTimer); searchTimer = setTimeout(runSearch, 220); });

document.addEventListener('keydown', (e) => {
  if (e.key !== '/' || e.metaKey || e.ctrlKey || e.altKey) return;
  if (e.target.closest('input, textarea, select, [contenteditable]') || $('dialog[open]')) return;
  e.preventDefault();
  openSearch();
});

/* ---------- product cards: soft 3D tilt and pearl highlight ---------- */
document.addEventListener('pointermove', (e) => {
  if (!finePointer.matches || reduceMotion.matches) return;
  const media = e.target.closest?.('.card')?.querySelector('.card-media');
  if (!media) return;
  const r = media.getBoundingClientRect();
  const x = (e.clientX - r.left) / r.width, y = (e.clientY - r.top) / r.height;
  media.style.setProperty('--ry', `${(x - .5) * 9}deg`);
  media.style.setProperty('--rx', `${(.5 - y) * 7}deg`);
  media.style.setProperty('--mx', `${x * 100}%`);
  media.style.setProperty('--my', `${y * 100}%`);
}, { passive: true });
document.addEventListener('pointerout', (e) => {
  const card = e.target.closest?.('.card');
  if (!card || card.contains(e.relatedTarget)) return;
  const media = card.querySelector('.card-media');
  media?.style.setProperty('--ry', '0deg');
  media?.style.setProperty('--rx', '0deg');
});

/* ---------- boutique grid: category, finish and sort, all client-side ---------- */
function setupBoutique(root) {
  const grid = $('[data-grid]', root);
  const cards = $$('[data-card]', grid);
  const filter = { cat: 'all', finish: 'all', sort: 'featured' };
  cards.forEach((c, i) => { c.dataset.index = i; });

  const apply = ({ animate = true } = {}) => {
    const visible = cards.filter((c) => (filter.cat === 'all' || c.dataset.type === filter.cat) && (filter.finish === 'all' || c.dataset.finish === filter.finish));
    const sorted = [...visible].sort((a, b) => {
      if (filter.sort === 'price-asc') return a.dataset.price - b.dataset.price;
      if (filter.sort === 'price-desc') return b.dataset.price - a.dataset.price;
      if (filter.sort === 'name') return a.dataset.title.localeCompare(b.dataset.title);
      return a.dataset.index - b.dataset.index;
    });
    cards.forEach((c) => { c.hidden = !visible.includes(c); c.classList.remove('is-entering'); });
    sorted.forEach((c, i) => {
      grid.append(c);
      if (animate && !reduceMotion.matches) { c.style.animationDelay = `${Math.min(i, 10) * 40}ms`; void c.offsetWidth; c.classList.add('is-entering'); }
    });
    $$('[data-cat]', root).forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.cat === filter.cat)));
    $$('[data-finish-filter]', root).forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.finishFilter === filter.finish)));
    const count = $('[data-count]', root);
    if (count) count.textContent = plural(visible.length, 'product');
    const empty = $('[data-empty]', root);
    if (empty) empty.hidden = visible.length > 0;
  };

  root.addEventListener('click', (e) => {
    const cat = e.target.closest('[data-cat]');
    if (cat) { filter.cat = cat.dataset.cat; apply(); return; }
    const fin = e.target.closest('[data-finish-filter]');
    if (fin) { filter.finish = fin.dataset.finishFilter; apply(); return; }
    if (e.target.closest('[data-reset]')) { filter.cat = 'all'; filter.finish = 'all'; apply(); }
  });
  $('[data-sort]', root)?.addEventListener('change', (e) => { filter.sort = e.target.value; apply(); });
  root.boutique = { setFinish(key) { filter.cat = 'all'; filter.finish = key; apply(); root.scrollIntoView({ behavior: scrollBehavior(), block: 'start' }); } };
}
$$('[data-boutique]').forEach(setupBoutique);

/* ---------- hero window ---------- */
let hero3D = null;
const heroState = { idx: 0, paused: reduceMotion.matches, hold: false, products: [] };

function setupHero(root) {
  try { heroState.products = JSON.parse($('[data-hero-products]', root)?.textContent || '[]'); } catch { heroState.products = []; }
  const items = heroState.products;
  const card = $('.window-card', root);
  if (!items.length || !card) return;

  const show = (i) => {
    heroState.idx = (i + items.length) % items.length;
    const p = items[heroState.idx];
    const link = $('[data-window-link]', card);
    link.textContent = p.title;
    link.href = p.url;
    $('[data-window-meta]', card).innerHTML = p.meta;
    $('[data-window-price]', card).innerHTML = p.price;
    $('[data-window-index]', card).textContent = `${heroState.idx + 1} / ${items.length}`;
    const addBtn = $('[data-window-add]', card), optLink = $('[data-window-options]', card);
    addBtn.hidden = !(p.single && p.available);
    addBtn.dataset.addVariant = p.variant;
    addBtn.setAttribute('aria-label', `Add ${p.title} to bag`);
    optLink.hidden = p.single && p.available;
    optLink.href = p.url;
    optLink.textContent = p.available ? 'Choose Options' : 'Sold Out';
    const archImg = $('.arch-frame img', root);
    if (archImg && p.image) { archImg.src = p.image; archImg.removeAttribute('srcset'); archImg.alt = p.alt; }
    if (p.image) hero3D?.setPhoto(p.image);
  };
  const pauseBtn = $('[data-window-pause]', card);
  const syncPause = () => {
    pauseBtn.setAttribute('aria-label', heroState.paused ? 'Play motion' : 'Pause motion');
    pauseBtn.innerHTML = heroState.paused ? ICON.play : ICON.pause;
  };
  let timer = 0;
  const schedule = () => {
    clearInterval(timer);
    if (items.length < 2) return;
    timer = setInterval(() => {
      if (!heroState.paused && !heroState.hold && document.visibilityState === 'visible') show(heroState.idx + 1);
    }, (Number(root.dataset.interval) || 7) * 1000);
  };
  $('[data-window-prev]', card)?.addEventListener('click', () => { show(heroState.idx - 1); schedule(); });
  $('[data-window-next]', card)?.addEventListener('click', () => { show(heroState.idx + 1); schedule(); });
  pauseBtn?.addEventListener('click', () => { heroState.paused = !heroState.paused; syncPause(); hero3D?.refresh(); });
  const stage = $('.hero-stage', root);
  stage.addEventListener('pointerenter', () => { heroState.hold = true; });
  stage.addEventListener('pointerleave', () => { heroState.hold = false; });
  stage.addEventListener('focusin', () => { heroState.hold = true; });
  stage.addEventListener('focusout', () => { heroState.hold = false; });
  syncPause();
  show(0);
  schedule();
}
const heroRoot = $('[data-hero]');
if (heroRoot) setupHero(heroRoot);

/* ---------- texture lab ---------- */
let lab3D = null;
const labState = { paused: false, preset: 'mul' };

function setupLab(root) {
  const tabs = $$('[role="radio"]', root);
  if (!tabs.length) return;
  const select = (tab, focus = false) => {
    tabs.forEach((b) => {
      const on = b === tab;
      b.setAttribute('aria-checked', String(on));
      b.tabIndex = on ? 0 : -1;
      $(`#${b.getAttribute('aria-controls')}`).hidden = !on;
    });
    if (focus) tab.focus();
    labState.preset = tab.dataset.preset;
    lab3D?.setFinish(labState.preset);
  };
  root.addEventListener('click', (e) => {
    const tab = e.target.closest('[role="radio"]');
    if (tab) { select(tab); return; }
    const shop = e.target.closest('[data-shop-finish]');
    const boutique = $('[data-boutique]');
    if (shop && boutique?.boutique) { e.preventDefault(); boutique.boutique.setFinish(shop.dataset.shopFinish); }
  });
  $('.finish-tabs', root).addEventListener('keydown', (e) => {
    let i = tabs.findIndex((b) => b.getAttribute('aria-checked') === 'true');
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') i = (i + 1) % tabs.length;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') i = (i - 1 + tabs.length) % tabs.length;
    else if (e.key === 'Home') i = 0;
    else if (e.key === 'End') i = tabs.length - 1;
    else return;
    e.preventDefault();
    select(tabs[i], true);
  });
  labState.preset = (tabs.find((b) => b.getAttribute('aria-checked') === 'true') || tabs[0]).dataset.preset;

  const pause = $('[data-lab-pause]', root);
  pause?.addEventListener('click', () => {
    labState.paused = !labState.paused;
    pause.setAttribute('aria-label', labState.paused ? 'Play motion' : 'Pause motion');
    pause.innerHTML = labState.paused ? ICON.play : ICON.pause;
    lab3D?.refresh();
  });
  $('[data-lab-squish]', root)?.addEventListener('click', () => lab3D?.squish());
  $('.lab-controls', root)?.addEventListener('keydown', (e) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    lab3D?.nudge(e.key === 'ArrowRight' ? 1 : -1);
  });
}
const labRoot = $('[data-lab]');
if (labRoot) setupLab(labRoot);

/* ---------- ritual ---------- */
$$('[data-ritual-add]').forEach((btn) => btn.addEventListener('click', async () => {
  const ids = (btn.dataset.ritualAdd || '').split(',').map(Number).filter(Boolean);
  if (!ids.length) return;
  btn.classList.add('is-loading');
  try { await addToCart({ items: ids.map((id) => ({ id, quantity: 1 })) }); }
  catch (err) { toast(err.message); }
  finally { btn.classList.remove('is-loading'); }
}));

/* ---------- glow diary: curve the strip like an arcade ---------- */
$$('[data-diary]').forEach((root) => {
  const track = $('.diary-track', root);
  if (!track) return;
  let raf = 0;
  const curve = () => {
    raf = 0;
    if (reduceMotion.matches) return;
    const items = [...track.children];
    const half = track.clientWidth / 2;
    const center = track.scrollLeft + half;
    const offsets = items.map((it) => Math.max(-1.6, Math.min(1.6, (it.offsetLeft + it.offsetWidth / 2 - center) / half)));
    items.forEach((it, i) => { it.style.setProperty('--o', offsets[i].toFixed(3)); it.style.setProperty('--oa', Math.abs(offsets[i]).toFixed(3)); });
  };
  const queue = () => { if (!raf) raf = requestAnimationFrame(curve); };
  track.addEventListener('scroll', queue, { passive: true });
  addEventListener('resize', queue);
  const stepSize = () => (track.firstElementChild?.getBoundingClientRect().width || 300) + 20;
  $('[data-diary-prev]', root)?.addEventListener('click', () => track.scrollBy({ left: -stepSize(), behavior: scrollBehavior() }));
  $('[data-diary-next]', root)?.addEventListener('click', () => track.scrollBy({ left: stepSize(), behavior: scrollBehavior() }));
  queue();
});

/* ---------- product page ---------- */
function setupProduct(root) {
  let product;
  try { product = JSON.parse($('[data-product-json]', root).textContent); } catch { return; }
  const form = $('[data-product-form]', root);
  const idInput = $('[name="id"]', form);
  const addBtn = $('[data-add-button]', root);
  const status = $('[data-form-status]', root);
  const priceEl = $('[data-price]', root);
  const radios = $$('input[data-option-index]', root);

  const selected = () => product.options.map((_, i) => radios.find((r) => Number(r.dataset.optionIndex) === i && r.checked)?.value);
  const showMedia = (mediaId) => {
    const thumb = $(`[data-media-id="${mediaId}"]`, root);
    if (thumb) thumb.click();
  };
  const markAvailability = (values) => {
    radios.forEach((r) => {
      const i = Number(r.dataset.optionIndex);
      const ok = product.variants.some((v) => v.available && v.options[i] === r.value && v.options.every((val, j) => j === i || val === values[j]));
      r.closest('.opt')?.classList.toggle('is-unavailable', !ok);
    });
  };
  const update = () => {
    const values = selected();
    const variant = product.variants.find((v) => v.options.every((val, i) => val === values[i]));
    values.forEach((v, i) => { const out = $(`[data-option-value="${i}"]`, root); if (out) out.textContent = v; });
    markAvailability(values);
    if (!variant) {
      addBtn.disabled = true;
      addBtn.textContent = 'Unavailable';
      return;
    }
    idInput.value = variant.id;
    addBtn.disabled = !variant.available;
    addBtn.textContent = variant.available ? 'Add to Bag' : 'Sold Out';
    priceEl.innerHTML = formatMoney(variant.price) + (variant.compare_at_price > variant.price ? ` <s class="price-was">${formatMoney(variant.compare_at_price)}</s>` : '');
    if (variant.featured_media) showMedia(variant.featured_media.id);
    const url = new URL(location.href);
    url.searchParams.set('variant', variant.id);
    history.replaceState(history.state, '', url);
  };
  radios.forEach((r) => r.addEventListener('change', update));
  if (radios.length) markAvailability(selected());

  root.addEventListener('click', (e) => {
    const thumb = e.target.closest('[data-media-id]');
    if (thumb) {
      const main = $('[data-main-media] img', root);
      if (main) { main.src = thumb.dataset.src; main.srcset = thumb.dataset.srcset || ''; main.alt = thumb.dataset.alt; }
      $$('[data-media-id]', root).forEach((b) => b.setAttribute('aria-current', String(b === thumb)));
      return;
    }
    const step = e.target.closest('[data-qty-step]');
    if (step) {
      const input = $('[name="quantity"]', form);
      input.value = Math.max(1, (Number(input.value) || 1) + Number(step.dataset.qtyStep));
    }
  });

  form?.addEventListener('submit', async (e) => {
    if (!bag) return;
    e.preventDefault();
    addBtn.classList.add('is-loading');
    addBtn.setAttribute('aria-disabled', 'true');
    status.classList.remove('is-error');
    try {
      await addToCart(new FormData(form));
      status.textContent = 'Added to your bag.';
    } catch (err) {
      status.textContent = err.message;
      status.classList.add('is-error');
    } finally {
      addBtn.classList.remove('is-loading');
      addBtn.removeAttribute('aria-disabled');
    }
  });
}
$$('[data-product-root]').forEach(setupProduct);

/* ---------- product recommendations ---------- */
$$('[data-recommendations]').forEach(async (el) => {
  try {
    const html = await fetch(el.dataset.url).then((r) => r.text());
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('[data-recommendations]');
    if (fresh && fresh.querySelector('[data-card]')) { el.innerHTML = fresh.innerHTML; syncSaveButtons(); }
    else el.closest('.shopify-section')?.remove();
  } catch { el.closest('.shopify-section')?.remove(); }
});

/* ---------- sort selects submit their form; address country selects show the saved country ---------- */
$$('[data-autosubmit]').forEach((s) => s.addEventListener('change', () => s.form.requestSubmit()));
$$('select[data-default]').forEach((s) => { if (s.dataset.default) s.value = s.dataset.default; });

/* ---------- gentle entrance for content below the fold; everything stays visible without it ---------- */
if (!reduceMotion.matches && 'IntersectionObserver' in window) {
  const io = new IntersectionObserver((entries) => entries.forEach((en) => {
    if (en.isIntersecting) { en.target.classList.remove('is-pre'); io.unobserve(en.target); }
  }), { rootMargin: '0px 0px -6% 0px' });
  $$('[data-reveal]').forEach((el) => {
    if (el.getBoundingClientRect().top > innerHeight * 1.02) { el.classList.add('is-pre'); io.observe(el); }
  });
}

syncSaveButtons();

/* ---------- 3D scenes ---------- */
async function boot3D() {
  if (!B.enable3d || (!heroRoot && !labRoot)) return;
  if (!document.createElement('canvas').getContext('webgl2')) return;
  let scenes;
  try { scenes = await import(B.sceneModule); }
  catch (err) { console.warn('Glow On: 3D scenes unavailable, showing photo fallbacks.', err); return; }
  const photos = heroState.products.map((p) => p.image).filter(Boolean);
  if (heroRoot && photos.length && $('.hero-canvas', heroRoot)) {
    try {
      hero3D = scenes.initHero({ hero: heroRoot, canvas: $('.hero-canvas', heroRoot), frameEl: $('.arch-frame', heroRoot), photos, startIndex: heroState.idx, isPaused: () => heroState.paused });
    } catch (err) { console.warn('Glow On: hero scene failed', err); }
  }
  if (labRoot && $('.lab-canvas', labRoot)) {
    try { lab3D = scenes.initLab({ stage: $('.lab-stage', labRoot), canvas: $('.lab-canvas', labRoot), finish: labState.preset, isPaused: () => labState.paused }); }
    catch (err) { console.warn('Glow On: lab scene failed', err); }
  }
}
boot3D();

/* theme editor: re-run setup when a section is re-rendered */
document.addEventListener('shopify:section:load', (e) => {
  const el = e.target;
  $$('[data-boutique]', el).forEach(setupBoutique);
  $$('[data-product-root]', el).forEach(setupProduct);
  syncSaveButtons();
});
